/**
 * User settings of the code editors (rp-editor-code and its descendants): what the completion offers, the look of
 * Monaco. They are ordinary settings of the users service (ids `editor.*`, registered with the core specs), so an
 * admin can set them for a role or a company too; the gear menu of the editor (editorSettingsMenu.ts) edits the
 * personal (user scope) values and every open editor follows the change through the `change` event of rp.settings.
 */
import { msg } from "@omegagrid/localize";
import type { SqlKeywordCase } from "@reporting/rp-utils-sql-completion";
import rp from "../global";
import type { SettingsItem } from "../types";
import type { SqlMessagesLevel } from "../client/sqlStream";

export type EditorSettingSpec = SettingsItem & {
	/** label in the gear menu of the editor */
	label: () => string,
	/** shown only in editors of the SQL language */
	sql?: boolean,
};

/** the setting that switches the column type tooltips of the result grids on and off (rp-editor-sql-result listens to it) */
export const RESULT_TYPES_SETTING = 'editor.sql.result_types';

export const EDITOR_SETTINGS: EditorSettingSpec[] = [{
	id: 'editor.completion',
	label: () => msg('Code completion'),
	description: 'Code completion (suggestions while typing) in the code editors',
	type: 'b',
	defaultValue: true,
	hidden: true,
}, {
	id: 'editor.completion.document_words',
	label: () => msg('Words of the document'),
	description: 'Offer the words already written in the document in the code completion',
	type: 'b',
	defaultValue: true,
	hidden: true,
}, {
	id: 'editor.completion.schema',
	label: () => msg('Database objects'),
	description: 'Offer the tables, views, procedures and functions of the databases in the SQL completion',
	type: 'b',
	defaultValue: true,
	hidden: true,
	sql: true,
}, {
	id: 'editor.sql.keyword_case',
	label: () => msg('Keyword case'),
	description: 'Case of the SQL keywords, types and functions the completion inserts',
	type: 's',
	defaultValue: 'auto',
	content: [
		{k: 'auto', v: 'as typed'},
		{k: 'upper', v: 'UPPER'},
		{k: 'lower', v: 'lower'},
	],
	hidden: true,
	sql: true,
}, {
	id: 'editor.sql.signatures',
	label: () => msg('Function signatures'),
	description: 'Insert the SQL functions with placeholders for their arguments',
	type: 'b',
	defaultValue: true,
	hidden: true,
	sql: true,
}, {
	id: 'editor.sql.messages',
	label: () => msg('Result messages'),
	description: 'Which messages of an executed SQL batch get a result tab: the errors only, the errors and the warnings, or everything including the PRINT output',
	type: 's',
	defaultValue: 'warnings',
	content: [
		{k: 'errors', v: 'errors only'},
		{k: 'warnings', v: 'errors and warnings'},
		{k: 'all', v: 'all'},
	],
	hidden: true,
	sql: true,
}, {
	id: RESULT_TYPES_SETTING,
	label: () => msg('Column types in results'),
	description: 'Show the declared data type of a column (nvarchar(50), decimal(18,4), NULL / NOT NULL) in a tooltip of its header in the result grids of the SQL editors',
	type: 'b',
	defaultValue: false,
	hidden: true,
	sql: true,
}, {
	id: 'editor.minimap',
	label: () => msg('Minimap'),
	description: 'Minimap (code outline) at the right edge of the code editors',
	type: 'b',
	defaultValue: true,
	hidden: true,
}, {
	id: 'editor.word_wrap',
	label: () => msg('Word wrap'),
	description: 'Wrap long lines in the code editors',
	type: 'b',
	defaultValue: false,
	hidden: true,
}, {
	id: 'editor.font_size',
	label: () => msg('Font size'),
	description: 'Font size of the code editors in pixels (empty = default)',
	type: 'n',
	defaultValue: null,
	hidden: true,
}];

/** the settings specs to register (without the menu metadata) */
export const EDITOR_SETTINGS_SPECS: SettingsItem[] = EDITOR_SETTINGS.map(({ label, sql, ...spec }) => spec);

export type EditorSettings = {
	completion: boolean,
	documentWords: boolean,
	schema: boolean,
	keywordCase: SqlKeywordCase,
	signatures: boolean,
	messages: SqlMessagesLevel,
	resultTypes: boolean,
	minimap: boolean,
	wordWrap: boolean,
	fontSize: number | null,
};

export const isEditorSetting = (id: string) => id.startsWith('editor.');

const bool = (id: string) => rp.settings.get(id) !== false && rp.settings.get(id) !== 0 && rp.settings.get(id) !== '0';

/** the current values (user scope merged with the inherited ones), defaults for what is not set */
export function getEditorSettings(): EditorSettings {
	const keywordCase = rp.settings.get('editor.sql.keyword_case');
	const messages = rp.settings.get('editor.sql.messages');
	const fontSize = Number(rp.settings.get('editor.font_size'));
	return {
		completion: bool('editor.completion'),
		documentWords: bool('editor.completion.document_words'),
		schema: bool('editor.completion.schema'),
		keywordCase: keywordCase === 'upper' || keywordCase === 'lower' ? keywordCase : 'auto',
		signatures: bool('editor.sql.signatures'),
		messages: messages === 'errors' || messages === 'all' ? messages : 'warnings',
		resultTypes: bool(RESULT_TYPES_SETTING),
		minimap: bool('editor.minimap'),
		wordWrap: rp.settings.get('editor.word_wrap') === true || rp.settings.get('editor.word_wrap') === 1 || rp.settings.get('editor.word_wrap') === '1',
		fontSize: fontSize > 0 ? fontSize : null,
	};
}

/**
 * The Monaco options the settings stand for. With the completion off Monaco's automatic suggestions go as well
 * (Ctrl+Space still lists the words of the document: the bundled Monaco ignores `wordBasedSuggestions` in
 * updateOptions(), the switch of the document words is honoured by the adapter's provider, see CodeEditor).
 * `defaultFontSize` is the size og-code created the editor with (the font size setting empty = back to it);
 * unknown before the editor is ready, the font size is then left alone.
 */
export function createMonacoOptions(settings: EditorSettings, compact: boolean, defaultFontSize?: number): Record<string, unknown> {
	const fontSize = settings.fontSize ?? defaultFontSize;
	return {
		minimap: { enabled: settings.minimap && !compact },
		wordWrap: settings.wordWrap ? 'on' : 'off',
		...(fontSize ? { fontSize } : {}),
		quickSuggestions: settings.completion,
		suggestOnTriggerCharacters: settings.completion,
	};
}
